import { useEffect, useMemo } from "react";
import {
    OrganizationConnectionGroupBy,
    OrganizationConnectionItemSort,
    OrganizationIntegrationAccountItem,
} from "@common/schemas/organization-connection-groups.schema";
import { GroupChildrenState } from "@design-system/ViewTable/grouping/groupByRowTypes";
import { useMinimumVisibleLoading } from "@hooks/useMinimumVisibleLoading";
import { useOrgConnectionGroupItems } from "@api/organizationIntegration.api";
import { OrganizationConnectionFilters } from "@common/schemas/organization-connection-filters.schema";

export interface GroupChildrenControls {
    fetchNextPage: () => void;
    retry: () => void;
}

interface GroupChildrenLoaderProps {
    organizationId: string;
    groupBy: OrganizationConnectionGroupBy;
    groupKey: string;
    enabled: boolean;
    filters?: OrganizationConnectionFilters;
    sort?: OrganizationConnectionItemSort;
    onStateChange: (
        groupKey: string,
        state: GroupChildrenState<OrganizationIntegrationAccountItem>,
    ) => void;
    registerControls: (
        groupKey: string,
        controls: GroupChildrenControls,
    ) => void;
    unregisterControls: (groupKey: string) => void;
}

/**
 * Renders nothing — runs one group's paginated query and lifts its state up.
 * The query is enabled only while the group is expanded. On collapse the last
 * loaded state is kept (not cleared), so re-expanding shows the loaded rows
 * immediately instead of flashing skeletons; groups that were never expanded
 * stay at `undefined` state and show skeletons on first expand.
 *
 * Owns the lifetime of its registered controls: they are registered on mount
 * and unregistered on unmount, so a caller never has to clear them itself.
 */
export const GroupChildrenLoader = ({
    organizationId,
    groupBy,
    groupKey,
    enabled,
    filters,
    sort,
    onStateChange,
    registerControls,
    unregisterControls,
}: GroupChildrenLoaderProps) => {
    const query = useOrgConnectionGroupItems({
        organizationId,
        groupBy,
        groupKey,
        enabled,
        filters,
        sort,
    });

    const items = useMemo(
        () => (query.data?.pages ?? []).flatMap((page) => page.items),
        [query.data],
    );

    const { fetchNextPage, refetch } = query;
    useEffect(() => {
        registerControls(groupKey, {
            fetchNextPage: () => {
                void fetchNextPage();
            },
            retry: () => {
                void refetch();
            },
        });
        return () => unregisterControls(groupKey);
    }, [
        groupKey,
        fetchNextPage,
        refetch,
        registerControls,
        unregisterControls,
    ]);

    const hasMore = query.hasNextPage;
    const { isError, isSuccess } = query;
    const isLoadingFirstPage = useMinimumVisibleLoading(query.isLoading);
    const isLoadingMore = useMinimumVisibleLoading(query.isFetchingNextPage);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        onStateChange(groupKey, {
            items,
            hasMore,
            isLoadingFirstPage,
            isLoadingMore,
            isError,
            isSettled: isSuccess,
        });
    }, [
        enabled,
        groupKey,
        items,
        hasMore,
        isLoadingFirstPage,
        isLoadingMore,
        isError,
        isSuccess,
        onStateChange,
    ]);

    return null;
};
